import type { ReactNode } from 'react';
import { createElement } from 'react';
import { Icon } from '@phosphor-icons/react';
import { twMerge } from 'tailwind-merge';
import Panel from './Panel';

const SIZE = {
  sm: { icon: 16, title: 'text-xs', description: 'text-xs', gap: 'gap-3' },
  md: { icon: 18, title: 'text-sm', description: 'text-xs', gap: 'gap-3' },
  lg: { icon: 24, title: 'text-base', description: 'text-sm', gap: 'gap-4' }
} as const;

type CompoundButtonProps = {
  icon?: Icon;
  title: string;
  description?: ReactNode;
  onClick?: () => void;
  className?: string;
  /** Background class for the icon well, e.g. `bg-emerald-100`. */
  iconClassName?: string;
  trailing?: ReactNode;
  disabled?: boolean;
  /** `sm`/`md` match `Button` label sizes. `lg` is text-base (folder cards). */
  size?: 'sm' | 'md' | 'lg';
};

/** A full-width button with an icon, title, and optional description. */
export default function CompoundButton({
  icon,
  title,
  description,
  onClick,
  className,
  iconClassName = 'bg-bluegray-300',
  trailing,
  disabled = false,
  size = 'md'
}: CompoundButtonProps) {
  const tokens = SIZE[size];
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={twMerge(
        'group flex h-full w-full text-left',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1',
        'disabled:cursor-not-allowed disabled:opacity-50'
      )}
    >
      <Panel
        size="sm"
        className={twMerge(
          'h-full w-full flex-row items-start transition-colors hover:bg-surface-hover',
          tokens.gap,
          className
        )}
      >
        {icon ? (
          <span
            className={twMerge('inline-flex shrink-0 items-center justify-center rounded-lg p-1.5', iconClassName)}
            aria-hidden
          >
            {createElement(icon, {
              size: tokens.icon,
              weight: 'light',
              className: 'text-content-default'
            })}
          </span>
        ) : null}
        <span className="min-w-0 flex-1">
          <span className={twMerge('block font-semibold leading-5 text-content-default', tokens.title)}>{title}</span>
          {description ? (
            <span className={twMerge('mt-0.5 block leading-snug text-content-subtle', tokens.description)}>
              {description}
            </span>
          ) : null}
        </span>
        {trailing}
      </Panel>
    </button>
  );
}
